import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Wrench } from "lucide-react";
import api from "../lib/api";
import { ENG_SECTIONS, FIRST_SECTION, FIRST_TAB, findSection, findTab } from "../components/engineering/_catalogue";
import { PANEL_STATUS, statusBadge, statusLabel, roadmapStats } from "../components/engineering/_status";
import PipingSpecsPanel from "../components/engineering/PipingSpecsPanel";
import PipeSupportsPanel from "../components/engineering/PipeSupportsPanel";
import WeldsPanel from "../components/engineering/WeldsPanel";
import DrawingRegisterPanel from "../components/engineering/DrawingRegisterPanel";
import IsometricPanel from "../components/engineering/drawings/IsometricPanel";
import HookUpPanel from "../components/engineering/drawings/HookUpPanel";
import GAPanel from "../components/engineering/drawings/GAPanel";
import PlotPlanPanel from "../components/engineering/drawings/PlotPlanPanel";
import ConsistencyCheckerPanel from "../components/engineering/sync/ConsistencyCheckerPanel";
import MTOPanel from "../components/engineering/sync/MTOPanel";
import StressScreenerPanel from "../components/engineering/sync/StressScreenerPanel";
import PointCloudPanel from "../components/engineering/sync/PointCloudPanel";
import CommandBarPanel from "../components/engineering/ai/CommandBarPanel";
import DesignReviewerPanel from "../components/engineering/ai/DesignReviewerPanel";
import AutoCompletePanel from "../components/engineering/ai/AutoCompletePanel";
import TemplateLibraryPanel from "../components/engineering/ai/TemplateLibraryPanel";
import PIDEditor from "../components/engineering/editors/PIDEditor";
import ModelEditor3D from "../components/engineering/editors/ModelEditor3D";
import SLDEditor from "../components/engineering/editors/SLDEditor";
import AlignmentEditorPanel from "../components/engineering/roads/AlignmentEditorPanel";
import ProfileEditorPanel from "../components/engineering/roads/ProfileEditorPanel";
import CrossSectionEditorPanel from "../components/engineering/roads/CrossSectionEditorPanel";
import CorridorViewerPanel from "../components/engineering/roads/CorridorViewerPanel";
import IntersectionPanel from "../components/engineering/roads/IntersectionPanel";
import BridgeModelerPanel from "../components/engineering/structures/BridgeModelerPanel";
import CulvertPanel from "../components/engineering/structures/CulvertPanel";
import RetainingWallPanel from "../components/engineering/structures/RetainingWallPanel";
import TerrainEditorPanel from "../components/engineering/structures/TerrainEditorPanel";
import EarthworkVizPanel from "../components/engineering/structures/EarthworkVizPanel";
import DrainageNetworkPanel from "../components/engineering/drainage/DrainageNetworkPanel";
import HydraulicProfilePanel from "../components/engineering/drainage/HydraulicProfilePanel";
import SWMMExportPanel from "../components/engineering/drainage/SWMMExportPanel";
import FloorPlanEditorPanel from "../components/engineering/architecture/FloorPlanEditorPanel";
import AreaStatementPanel from "../components/engineering/architecture/AreaStatementPanel";
import ElevationSectionPanel from "../components/engineering/architecture/ElevationSectionPanel";
import StructuralGridPanel from "../components/engineering/structural_re/StructuralGridPanel";
import StructuralElementsPanel from "../components/engineering/structural_re/StructuralElementsPanel";
import Structural3DPanel from "../components/engineering/structural_re/Structural3DPanel";
import HVACLayoutPanel from "../components/engineering/mep_re/HVACLayoutPanel";
import PlumbingLayoutPanel from "../components/engineering/mep_re/PlumbingLayoutPanel";
import ElectricalLayoutPanel from "../components/engineering/mep_re/ElectricalLayoutPanel";
import MEPRoutingPanel from "../components/engineering/mep_re/MEPRoutingPanel";
import RebarDrawingsPanel from "../components/engineering/structural_re/RebarDrawingsPanel";
import SitePlanPanel from "../components/engineering/site_re/SitePlanPanel";
import BIMCoordinationPanel from "../components/engineering/bim/BIMCoordinationPanel";
import AIDomainPanel from "../components/engineering/ai_domain/AIDomainPanel";
import GenerativeDesignPanel from "../components/engineering/generative/GenerativeDesignPanel";
import DocumentationPanel from "../components/DocumentationPanel";
import CostEstimationPanel from "../components/CostEstimationPanel";

const PANELS = {
  specs: PipingSpecsPanel,
  supports: PipeSupportsPanel,
  welds: WeldsPanel,
  drawings: DrawingRegisterPanel,
  iso: IsometricPanel,
  hookup: HookUpPanel,
  ga: GAPanel,
  plotplan: PlotPlanPanel,
  sync: ConsistencyCheckerPanel,
  mto: MTOPanel,
  stress: StressScreenerPanel,
  pointcloud: PointCloudPanel,
  command: CommandBarPanel,
  review: DesignReviewerPanel,
  autocomplete: AutoCompletePanel,
  templates: TemplateLibraryPanel,
  "pid-editor": PIDEditor,
  "model-editor": ModelEditor3D,
  "sld-editor": SLDEditor,
  "alignment": AlignmentEditorPanel,
  "profile": ProfileEditorPanel,
  "cross-section": CrossSectionEditorPanel,
  "corridor": CorridorViewerPanel,
  "intersections": IntersectionPanel,
  "bridges": BridgeModelerPanel,
  "culverts": CulvertPanel,
  "retaining-walls": RetainingWallPanel,
  "terrain": TerrainEditorPanel,
  "earthwork-viz": EarthworkVizPanel,
  "drainage-network": DrainageNetworkPanel,
  "hydraulic-profile": HydraulicProfilePanel,
  "swmm-export": SWMMExportPanel,
  "floor-plan": FloorPlanEditorPanel,
  "area-statement": AreaStatementPanel,
  "elevation": ElevationSectionPanel,
  "structural-grid": StructuralGridPanel,
  "structural-elements": StructuralElementsPanel,
  "structural-3d": Structural3DPanel,
  "hvac-layout": HVACLayoutPanel,
  "plumbing-layout": PlumbingLayoutPanel,
  "electrical-layout": ElectricalLayoutPanel,
  "mep-routing": MEPRoutingPanel,
  "rebar-drawings": RebarDrawingsPanel,
  "site-plan": SitePlanPanel,
  "bim-coordination": BIMCoordinationPanel,
  "ai-infra": (props) => <AIDomainPanel {...props} domain="infra" />,
  "ai-re":    (props) => <AIDomainPanel {...props} domain="re" />,
  "generative-design": GenerativeDesignPanel,
  "documentation": DocumentationPanel,
  "cost-estimation": CostEstimationPanel,
};

export default function Engineering() {
  const { id: projectId, section, tab } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);

  // Resolve current section + tab — fall back to first valid combo on bad URLs
  let activeSection = findSection(section);
  let activeTab = activeSection?.tabs.find((t) => t.key === tab);
  // If tab is wrong/missing for the section → first tab in the section
  if (activeSection && !activeTab) activeTab = activeSection.tabs[0];
  // If section is missing entirely → redirect to first
  useEffect(() => {
    if (!activeSection) {
      nav(`/projects/${projectId}/engineering/${FIRST_SECTION}/${FIRST_TAB}`, { replace: true });
    } else if (!tab || !activeSection.tabs.some((t) => t.key === tab)) {
      nav(`/projects/${projectId}/engineering/${activeSection.key}/${activeSection.tabs[0].key}`, { replace: true });
    }
  }, [activeSection, tab, projectId, nav]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
  }, [projectId]);

  if (!activeSection || !activeTab) return null;
  const Panel = PANELS[activeTab.key];

  return (
    <div data-testid="engineering-shell"
         style={{ display: "grid", gridTemplateColumns: "260px 1fr", minHeight: "100vh",
                  background: "var(--pf-bg)", color: "var(--pf-fg)" }}>
      {/* SIDEBAR */}
      <aside style={{
        position: "sticky", top: 0, alignSelf: "start", height: "100vh", overflowY: "auto",
        background: "var(--pf-surface)", borderRight: "1px solid var(--pf-border)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Top bar — back + project name */}
        <div style={{ padding: 12, borderBottom: "1px solid var(--pf-border)" }}>
          <Link to={`/projects/${projectId}`} data-testid="eng-back-to-workspace"
                style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--pf-muted)",
                         textDecoration: "none", fontSize: 11, fontWeight: 600 }}>
            <ArrowLeft size={13} /> Back to workspace
          </Link>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
            <Wrench size={16} style={{ color: "#F26B1F" }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: "var(--pf-fg)" }}>Engineering</div>
              <div style={{ fontSize: 10, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {project?.name || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Sections + tabs */}
        <div style={{ flex: 1, padding: 6 }}>
          {/* Roadmap progress bar */}
          {(() => {
            const r = roadmapStats();
            return (
              <div data-testid="eng-roadmap-progress"
                    style={{
                      padding: "10px 10px",
                      margin: "4px 4px 10px",
                      borderRadius: 8,
                      background: "rgba(22,163,74,0.08)",
                      border: "1px solid rgba(22,163,74,0.4)",
                    }}>
                <div style={{
                  display: "flex", alignItems: "baseline",
                  justifyContent: "space-between", marginBottom: 6,
                }}>
                  <span style={{ fontSize: 9, fontWeight: 800,
                                 letterSpacing: 1,
                                 color: "var(--pf-muted)",
                                 textTransform: "uppercase" }}>
                    Backbone Roadmap
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700,
                                 color: "#16a34a",
                                 fontFamily: "JetBrains Mono" }}>
                    {r.working}/{r.total}
                  </span>
                </div>
                <div style={{
                  height: 6, borderRadius: 3,
                  background: "#1F2937", overflow: "hidden",
                  display: "flex",
                }}>
                  <div data-testid="eng-roadmap-bar-working"
                        style={{
                          width: `${(r.working / r.total) * 100}%`,
                          background: "#16a34a",
                        }} />
                  <div data-testid="eng-roadmap-bar-partial"
                        style={{
                          width: `${(r.partial / r.total) * 100}%`,
                          background: "#FFB000",
                        }} />
                  <div data-testid="eng-roadmap-bar-uionly"
                        style={{
                          width: `${(r.ui_only / r.total) * 100}%`,
                          background: "#dc2626",
                        }} />
                </div>
                <div style={{ marginTop: 6, fontSize: 9,
                              color: "var(--pf-muted)",
                              display: "flex", gap: 8,
                              flexWrap: "wrap" }}>
                  <span style={{ color: "#16a34a" }}>
                    ● {r.working} working
                  </span>
                  {r.partial > 0 && (
                    <span style={{ color: "#FFB000" }}>
                      ● {r.partial} partial
                    </span>
                  )}
                  {r.ui_only > 0 && (
                    <span style={{ color: "#dc2626" }}>
                      ● {r.ui_only} UI-only
                    </span>
                  )}
                  <span style={{ marginLeft: "auto", fontWeight: 700,
                                 color: r.pct === 100 ? "#16a34a"
                                                       : "#FFB000" }}>
                    {r.pct}%
                  </span>
                </div>
              </div>
            );
          })()}
          {ENG_SECTIONS.map((s) => (
            <div key={s.key} data-testid={`eng-side-section-${s.key}`} style={{ marginBottom: 8 }}>
              <div style={{
                padding: "6px 10px", fontSize: 9, fontWeight: 800, letterSpacing: 1,
                color: "var(--pf-muted)", textTransform: "uppercase",
              }}>
                {s.title}
              </div>
              {s.tabs.map((t) => {
                const Icon = t.icon;
                const isActive = activeSection.key === s.key && activeTab.key === t.key;
                const meta = PANEL_STATUS[t.key];
                return (
                  <Link key={t.key}
                        to={`/projects/${projectId}/engineering/${s.key}/${t.key}`}
                        data-testid={`eng-side-tab-${t.key}`}
                        title={meta ? `${statusLabel(meta.status)} — ${meta.note}` : undefined}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "7px 10px", fontSize: 12, fontWeight: 600,
                          textDecoration: "none", borderRadius: 6, marginBottom: 1,
                          background: isActive ? "rgba(242,107,31,.10)" : "transparent",
                          color: isActive ? "#F26B1F" : "var(--pf-fg)",
                          borderLeft: `3px solid ${isActive ? "#F26B1F" : "transparent"}`,
                        }}>
                    <Icon size={13} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{t.label}</span>
                    {meta && <span data-testid={`eng-side-tab-${t.key}-status-${meta.status.toLowerCase()}`}
                                    style={statusBadge(meta.status)} />}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ padding: 10, fontSize: 9, color: "var(--pf-muted)", borderTop: "1px solid var(--pf-border)" }}>
          5 sections · 19 tools — Capexara Process Industry Engineering Suite
        </div>
      </aside>

      {/* CONTENT */}
      <main style={{ padding: 16, overflowX: "hidden" }} data-testid="engineering-content">
        <div style={{
          padding: "10px 14px", marginBottom: 12,
          background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--pf-muted)" }}>
            <Link to={`/projects/${projectId}`} style={{ color: "var(--pf-muted)", textDecoration: "none" }}>
              {project?.name || "Project"}
            </Link>
            <span>›</span>
            <span style={{ color: "var(--pf-muted)" }}>{activeSection.title}</span>
            <span>›</span>
            <strong style={{ color: "#F26B1F" }} data-testid="eng-active-title">{activeTab.label}</strong>
            {PANEL_STATUS[activeTab.key] && (
              <span data-testid="eng-active-status" style={{
                marginLeft: 8, padding: "2px 8px", borderRadius: 999,
                fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                textTransform: "uppercase",
                background: PANEL_STATUS[activeTab.key].status === "WORKING"
                  ? "rgba(22,163,74,0.15)"
                  : PANEL_STATUS[activeTab.key].status === "PARTIAL"
                  ? "rgba(255,176,0,0.15)"
                  : "rgba(220,38,38,0.15)",
                color: PANEL_STATUS[activeTab.key].status === "WORKING"
                  ? "#16a34a"
                  : PANEL_STATUS[activeTab.key].status === "PARTIAL"
                  ? "#FFB000"
                  : "#dc2626",
                border: `1px solid ${PANEL_STATUS[activeTab.key].status === "WORKING"
                  ? "#16a34a"
                  : PANEL_STATUS[activeTab.key].status === "PARTIAL"
                  ? "#FFB000"
                  : "#dc2626"}`,
              }}>
                {PANEL_STATUS[activeTab.key].status === "UI_ONLY"
                  ? "UI ONLY" : PANEL_STATUS[activeTab.key].status}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)", marginTop: 4 }}>{activeTab.desc}</div>
          {PANEL_STATUS[activeTab.key]?.status !== "WORKING" && PANEL_STATUS[activeTab.key]?.note && (
            <div data-testid="eng-active-status-note"
                  style={{ fontSize: 11, color: "#FFB000", marginTop: 6,
                            fontStyle: "italic" }}>
              ⚠ Gap: {PANEL_STATUS[activeTab.key].note}
            </div>
          )}
        </div>
        <div data-testid={`eng-panel-${activeTab.key}`}>
          {Panel ? <Panel projectId={projectId} /> : <div>Unknown tool: {activeTab.key}</div>}
        </div>
      </main>
    </div>
  );
}
